import Vue from 'vue'
import Lambda from '@/services/Lambda'
import Laravel from '@/services/Laravel'
import _get from 'lodash/get'
import _snakeCase from 'lodash/snakeCase'
import _includes from 'lodash/includes'
import { SQUARE } from '@/constants/PaymentProviders'
import { SQUARE_APP_ID } from '@/constants/credentials'
import { INTERNAL_USE_CATEGORY } from '@/constants/AppItemNames'
import FeatureFlags from '@/constants/FeatureFlags'
import ShopSettings from '@/constants/ShopSettings'
import SectionModel from '@/models/SectionModel'
import ShopModel from '@/models/ShopModel'
import Mtxs from '@/constants/Mutations'

export const state = {
  shop: null,
  isShopLoading: false,
  shopRoot: `${window.location.protocol}//${window.location.host}`,
  slug: window.location.hostname.split('.')[0],
  shopFetchError: false,
  dirtyFields: {},
} // state

export const mutations = {
  [Mtxs.SET_SHOP](state, shop) {
    state.shop = shop
  },
  [Mtxs.SET_SHOP_LOADING_STATE](state, isShopLoading) {
    state.isShopLoading = isShopLoading
  },
  [Mtxs.SET_SHOP_FETCH_ERROR](state, error) {
    state.shopFetchError = error
  },
  [Mtxs.UPDATE_SHOP_SETTING](state, { name, val }) {
    Vue.set(state.shop, name, val)
  },

  [Mtxs.ADD_TO_DIRTIES](state, { fieldName, changes }) {
    Vue.set(state.dirtyFields, _snakeCase(fieldName), changes)
  }, // ADD_TO_DIRTIES

  [Mtxs.REMOVE_FROM_DIRTIES](state, fieldName) {
    Vue.delete(state.dirtyFields, _snakeCase(fieldName))
  }, // REMOVE_FROM_DIRTIES
} // mutations

export const getters = {
  getShop: (state) =>
    ShopModel.query()
      .where('slug', state.slug)
      .first(),
  getShopId: (state) => _get(state.shop, 'id'),
  getIsShopLoading: (state) => state.isShopLoading,
  getShopFetchError: (state) => state.shopFetchError,
  getShopTitle: (state, getters) =>
    getters.getShopDetail('Title', 'Minimart').trim(),
  getContactEmail: (state, getters) =>
    getters.getShopDetail('Contact Email', '').trim(),
  getShopDetail: (state, getters) => (key, defaultVal = undefined) => {
    // If API returns null, use the || operator to return defaultVal
    const shop = ShopModel.find(getters.getShopId)
    return _get(shop, _snakeCase(key), defaultVal) || defaultVal
  },
  getBooleanToggle: (state, getters) => (key) =>
    !!+getters.getShopDetail(key, 0),
  getShopMultiSelectDetail: (state, getters) => (key) => {
    let detail = getters.getShopDetail(key)
    if (!detail) return []
    return Array.isArray(detail)
      ? detail
      : detail.split(',').map((i) => i.trim())
  },
  getShopMetaDetails: (state, getters) => getters.getShopDetail('meta', {}),
  getFeatureFlags: (state, getters) =>
    _get(getters.getShopMetaDetails, 'feature_flags', []),
  getFeatureFlagStatus: (state, getters) => (featureName) => {
    let feature_flags = getters.getFeatureFlags
    let flag = feature_flags.find((flag) => flag.name === featureName)
    return flag ? flag.active : 0
  },
  getFeatureFlagServiceFee: (state, getters) =>
    getters.getFeatureFlagStatus(FeatureFlags.SERVICE_FEE),
  getFeatureFlagAllowGiftPurchases: (state, getters) =>
    getters.getFeatureFlagStatus(FeatureFlags.ALLOW_GIFT_PURCHASES),
  getFeatureFlagItemDates: (state, getters) =>
    getters.getFeatureFlagStatus(FeatureFlags.ITEM_DATES),
  getIsItemDatesToggleAllowed: (state, getters) =>
    getters.getBooleanToggle('allow_item_dates_toggle'),
  getEnableItemDates: (state, getters) =>
    getters.getBooleanToggle('Enable Item Dates') &&
    getters.getFeatureFlagItemDates,
  getLeadTimeMinutes: (state, getters) =>
    getters.getShopDetail('Lead Time Minutes', 0),
  getShouldSendNewOrderAlerts: (state, getters) =>
    getters.getBooleanToggle('New Order Alerts'),
  getShouldSendCustomerInvoice: (state, getters) =>
    getters.getBooleanToggle('Email Customer Invoices'),
  getShouldSendWelcomeEmail: (state, getters) =>
    getters.getBooleanToggle('Send Welcome Email'),
  getShopRoot: (state) => state.shopRoot,
  getSalesTaxPcnt: (state, getters) =>
    Number(getters.getShopDetail('Sales Tax', 0)),
  getShouldCapturePaymentInfo: (state, getters) =>
    getters.getBooleanToggle('Capture Payment Info'),
  getShouldHideAddressFields: (state, getters) =>
    getters.getBooleanToggle('Hide Address Fields'),
  getIsPhoneRequiredForSignup: (state, getters) =>
    getters.getBooleanToggle('Require Phone For Signup'),
  getIsMaintenanceModeOn: (state, getters) =>
    getters.getBooleanToggle('Maintenance Mode'),
  getIsAdvancedPaymentOn: (state, getters) =>
    getters.getBooleanToggle('Enable Advanced Payment'),
  getPaymentOptionStatus: (state, getters) => (paymentOptionName) => {
    let currentOpts = getters.getShopDetail('Payment Options', '').split(',')
    return _includes(currentOpts, paymentOptionName)
  },
  getIsSquarePaymentEnabled: (state, getters) =>
    getters.getBooleanToggle('Enable Square Payment'),
  getSquareLocationID: (state, getters) =>
    getters.getShopDetail('Square Location ID'),
  getSquareAppId: (state, getters) =>
    process.env.VUE_APP_NODE_ENV !== 'production' && !!SQUARE_APP_ID
      ? SQUARE_APP_ID
      : getters.getShopDetail('Square App Id'),
  getShowSquareButton: (state, getters) =>
    getters.getPaymentOptionStatus(SQUARE) &&
    getters.getSquareLocationID &&
    getters.getSquareAppId,

  getIsLiveChatEnabled: (state, getters) =>
    getters.getBooleanToggle('Enable Live Chat'),
  getLiveChatButtonId: (state, getters) =>
    getters.getShopDetail('Live Chat Button ID', 'z7czynj7'),
  getFullStoryId: (state, getters) => getters.getShopDetail('Fullstory Id'),
  getIsSubscribed: (state, getters) =>
    getters.getBooleanToggle('is_subscribed', false),
  getIsShopDead: (state, getters) => getters.getBooleanToggle('is_dead', false),
  getShopHasActiveTrial: (state, getters) =>
    getters.getBooleanToggle('has_active_trial', false),

  getLowStockWarningLevel: (state, getters) =>
    Number(getters.getShopDetail('Low Stock Warning', 0)),
  getOversoldNote: (state, getters) =>
    getters
      .getShopDetail(
        'Item Oversold Note',
        `Some items didn't make it into your order. Please see your Items Ordered section below for details.`
      )
      .trim(),

  getWelcomeEmailBody: (state, getters) =>
    getters.getShopDetail('Welcome Email Body', '').trim(),
  getIsBakeSale: (state, getters) => getters.getBooleanToggle('Is Bake Sale'),
  getRequirePrepay: (state, getters) =>
    getters.getBooleanToggle('Require Prepay'),
  getAllowGiftPurchases: (state, getters) =>
    getters.getBooleanToggle('Allow Gift Purchases'),
  getFulfillmentOptions: (state, getters) =>
    getters.getShopMultiSelectDetail('Fulfillment Options'),
  getFulfillmentSlotsList: (state, getters) => (fulfillmentType) =>
    getters.getShopDetail(`${fulfillmentType} Slots`),
  getPickupSlots: (state, getters) =>
    [...getters.getShopDetail('Pickup Slots', [])].sort(),
  getDeliverySlots: (state, getters) =>
    [...getters.getShopDetail('Delivery Slots', [])].sort(),
  getShippingSlots: (state, getters) =>
    [...getters.getShopDetail('Shipping Slots', [])].sort(),
  getRequireVersionMatch: (state, getters) =>
    getters.getBooleanToggle('Require App Version Match'),
  getPaymentOptions: (state, getters) =>
    getters.getShopDetail('Payment Options', '').split(','),
  getInstagram: (state, getters) => getters.getShopDetail('Instagram'),
  getTwitter: (state, getters) => getters.getShopDetail('Twitter'),
  getFacebook: (state, getters) => getters.getShopDetail('Facebook'),

  getDeliveryFee: (state, getters) => getters.getShopDetail('Delivery Fee'),
  getTipDefault: (state, getters) => getters.getShopDetail('Tip Default', 0),
  getTimezone: (state, getters) => getters.getShopDetail('Timezone', null),

  getMenu: () =>
    SectionModel.query()
      .where((s) => s.name !== INTERNAL_USE_CATEGORY)
      .with('items')
      .get(),
  getDirtyFields: (state) => state.dirtyFields,
  getDirtScore: (state) => Object.keys(state.dirtyFields).length,
  getVisibleSettings: (state, getters) => {
    let sectionNames = Object.keys(ShopSettings)
    let hiddenFieldLabels = []
    let visibleSettings = {}

    if (!getters.getFeatureFlagAllowGiftPurchases) {
      hiddenFieldLabels.push('Allow Gift Purchases')
    }
    if (
      !getters.getFeatureFlagItemDates ||
      !getters.getIsItemDatesToggleAllowed
    ) {
      hiddenFieldLabels.push('Enable Item Dates')
    }
    if (!getters.getFeatureFlagItemDates) {
      hiddenFieldLabels.push('Lead Time')
    }

    sectionNames.forEach(
      (key) =>
        (visibleSettings[key] = ShopSettings[key].filter(
          (setting) => !hiddenFieldLabels.includes(setting.label)
        ))
    )
    return visibleSettings
  }, // getVisibleSettings
} // getters

export const actions = {
  shopFetchError({ commit }, error) {
    let status = _get(error, 'response.status')
    let message = 'Connectivity : Error Response Status undefined'

    switch (status) {
      case 401:
        message = 'Misconfiguration (401 - Auth Required)'
        break
      case 404:
        message = 'Misconfiguration (404 - Shop Not Found)'
        break

      default:
        break
    }
    commit(Mtxs.SET_SHOP_FETCH_ERROR, message)
  },
  /* Shop */
  async fetchShop({ dispatch, commit, getters }, skipCache) {
    if (!skipCache && getters.getShop) {
      return
    }

    commit(Mtxs.SET_SHOP_LOADING_STATE, true)
    // TODO (ESS) : make this SSR compat
    const shopSlug = window.location.hostname.split('.')[0]
    try {
      await ShopModel.fetchBySlug(shopSlug)
      let shop = ShopModel.query()
        .where('slug', shopSlug)
        .first()
      commit(Mtxs.SET_SHOP, shop)
      return shop
    } catch (error) {
      dispatch('shopFetchError', error)
    } finally {
      commit(Mtxs.SET_SHOP_LOADING_STATE, false)
    }
  }, // fetchShop

  async fetchShopById({ dispatch, commit, getters }, shopId = null) {
    shopId = shopId || getters.getShopId

    if (!shopId) return
    commit(Mtxs.SET_SHOP_LOADING_STATE, true)
    try {
      await ShopModel.fetchById(shopId)
      let shop = ShopModel.find(shopId)

      commit(Mtxs.SET_SHOP, shop)
      return shop
    } catch (error) {
      if (_get(error, 'response.status') === 403) {
        dispatch('auth/logout', null, { root: true })
      } else {
        dispatch('shopFetchError', error)
      }
    } finally {
      commit(Mtxs.SET_SHOP_LOADING_STATE, false)
    }
  }, // fetchShopById

  async fetchShopSettings({ dispatch, commit, getters }, shopId = null) {
    shopId = getters.getShopId
    try {
      await ShopModel.fetchSettingsById(shopId)
      let shop = ShopModel.find(shopId)
      commit(Mtxs.SET_SHOP, shop)
      return shop
    } catch (error) {
      if (_get(error, 'response.status') === 403) {
        dispatch('auth/logout', null, { root: true })
      } else {
        dispatch('shopFetchError', error)
      }
    } finally {
      commit(Mtxs.SET_SHOP_LOADING_STATE, false)
    }
  }, // fetchShopSettings

  clearSettingsFromState({ getters }) {
    ShopModel.delete(getters.getShopId)
  }, // clearSettingsFromState

  async sendActivationLink({ getters }) {
    try {
      let adminEmail = getters.getContactEmail

      let sendRes = await Lambda.post('admin_activation_email', {
        adminEmail,
      })
      return sendRes
    } catch (error) {
      return Promise.reject(error)
    }
  }, // sendActivationLink

  async fetchMenu() {
    try {
      const shopSlug = window.location.hostname.split('.')[0]
      await SectionModel.fetchMenuByShopSlug(shopSlug)
    } catch (error) {
      throw Error(error)
    }
  }, // fetchMenu

  async stageEdit({ commit }, { fieldName, changes }) {
    commit(Mtxs.ADD_TO_DIRTIES, { fieldName, changes })
  }, // stageEdit

  async unstageEdit({ commit }, fieldName) {
    commit(Mtxs.REMOVE_FROM_DIRTIES, fieldName)
  }, // unstageEdit

  async saveAllSettings({ getters, dispatch }) {
    try {
      await ShopModel.updateModel(getters.getShopId, getters.getDirtyFields)
      // clear out dirties and dirt score
      dispatch('removeDirtyFields', getters.getDirtyFields)
    } catch (error) {
      return Promise.reject(error)
    }
  }, // saveAllSettings

  removeDirtyField({ commit }, fieldName) {
    commit(Mtxs.REMOVE_FROM_DIRTIES, fieldName)
  }, // removeDirtyField

  removeDirtyFields({ dispatch }, dirtyFields) {
    Object.keys(dirtyFields).forEach((fieldName) => {
      return dispatch('removeDirtyField', fieldName)
    })
  }, // removeDirtyField

  async updateSetting({ getters }, { name, val }) {
    try {
      await ShopModel.updateModel(getters.getShopId, { [name]: val })
    } catch (error) {
      return Promise.reject(error)
    }
  }, // updateSetting

  async requestCancelation(_, shopId) {
    try {
      let { data: message } = await Laravel.post(
        `/shops/${shopId}/subscription/cancel`
      )
      return message
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getChart({ getters }, chartSlug) {
    return Laravel.get(`shops/${getters.getShopId}/charts/${chartSlug}`)
  }, // getChart

  getChartNinetyDaySales({ dispatch }) {
    return dispatch('getChart', 'sales-90')
  },
  getChartThirtyDayTopProductsByRevenue({ dispatch }) {
    return dispatch('getChart', 'top-products-30')
  },
  getChartThirtyDaySalesOverview({ dispatch }) {
    return dispatch('getChart', 'sales-overview-30')
  },
  getChartThirtyDayNewVsReturningCustomers({ dispatch }) {
    return dispatch('getChart', 'new-vs-returning-customers-30')
  },
} // actions

export const namespaced = true
